import React, { useEffect, useState, useRef } from "react";
import api from "../services/api";
import { reproducirSonido, mostrarNotificacion, solicitarPermisoNotificaciones } from "../utils/notificationUtils.js";

const RequisicionesPendientes = () => {
  const [requisiciones, setRequisiciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" });
  const [modalDetalle, setModalDetalle] = useState(null);
  
  // ✅ NUEVO: Modal para seleccionar almacén
  const [modalProcesar, setModalProcesar] = useState(null);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [almacenSeleccionado, setAlmacenSeleccionado] = useState("");
  const [loadingUbicaciones, setLoadingUbicaciones] = useState(false);

  // Modal de rechazo directo
  const [modalRechazo, setModalRechazo] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState("");
  
  const cantidadPendientesRef = useRef(0);

  // Solicitar permiso de notificaciones al cargar
  useEffect(() => {
    solicitarPermisoNotificaciones();
  }, []);

  const cargarRequisiciones = async (silencioso = false) => {
    try {
      if (!silencioso) setLoading(true);
      
      const { data } = await api.get("/requisiciones");
      
      console.log("📊 Total requisiciones recibidas:", data.length);
      
      const requisicionesNuevas = data.filter(req => 
        req.estado !== undefined && req.estado !== null
      );
      
      console.log("✅ Requisiciones con sistema:", requisicionesNuevas.length);
      
      const pendientesActuales = requisicionesNuevas.filter(r => r.estado === "pendiente_procesar").length;
      
      if (cantidadPendientesRef.current > 0 && pendientesActuales > cantidadPendientesRef.current) {
        const cantidadNuevas = pendientesActuales - cantidadPendientesRef.current;
        reproducirSonido();
        mostrarNotificacion(
          "🔔 Nueva Requisición",
          `Tienes ${cantidadNuevas} requisición(es) pendiente(s) de procesar`
        );
      }
      
      cantidadPendientesRef.current = pendientesActuales;
      setRequisiciones(requisicionesNuevas);
      
    } catch (error) {
      console.error("❌ Error cargando requisiciones:", error);
      if (!silencioso) {
        setMensaje({ texto: "❌ Error al cargar requisiciones", tipo: "error" });
      }
    } finally {
      if (!silencioso) setLoading(false);
    }
  };

  // ✅ NUEVO: Cargar ubicaciones del material
  const cargarUbicacionesMaterial = async (materialId) => {
    try {
      setLoadingUbicaciones(true);
      const { data } = await api.get(`/materiales/${materialId}/ubicaciones`);
      return data.ubicaciones || [];
    } catch (error) {
      console.error("Error cargando ubicaciones:", error);
      return [];
    } finally {
      setLoadingUbicaciones(false);
    }
  };

  // ✅ MODIFICADO: Abrir modal de procesamiento
  const iniciarProcesamiento = async (req) => {
    // Si tiene múltiples materiales, cargamos ubicaciones del primero
    // (en el futuro manejaremos lotes con múltiples materiales)
    const materialId = req.detalles?.[0]?.material_id || req.material?.id;
    
    if (!materialId) {
      setMensaje({ texto: "❌ No se pudo obtener el material de la requisición", tipo: "error" });
      return;
    }

    const ubicacionesCargadas = await cargarUbicacionesMaterial(materialId);
    
    if (ubicacionesCargadas.length === 0) {
      setMensaje({ 
        texto: "⚠️ No hay stock disponible en ningún almacén para este material", 
        tipo: "error" 
      });
      return;
    }

    setUbicaciones(ubicacionesCargadas);
    setModalProcesar(req);
    setAlmacenSeleccionado(""); // Resetear selección
  };

  // ✅ NUEVO: Confirmar procesamiento con almacén seleccionado
  const confirmarProcesamiento = async () => {
    if (!almacenSeleccionado) {
      setMensaje({ texto: "⚠️ Debe seleccionar un almacén de origen", tipo: "error" });
      return;
    }

    if (!window.confirm("¿Está seguro de procesar esta requisición con el almacén seleccionado?")) {
      return;
    }

    try {
      const response = await api.put(`/requisiciones/procesar/${modalProcesar.id}`, {
        almacen_origen_id: parseInt(almacenSeleccionado)
      });
      
      setMensaje({ 
        texto: response.data.mensaje || "✅ Requisición procesada. Salida enviada al supervisor.", 
        tipo: "success" 
      });
      
      cargarRequisiciones();
      cerrarModalProcesar();
      setTimeout(() => setMensaje({ texto: "", tipo: "" }), 5000);
    } catch (error) {
      console.error("Error al procesar:", error);
      setMensaje({ 
        texto: "❌ " + (error.response?.data?.mensaje || "Error al procesar requisición"), 
        tipo: "error" 
      });
    }
  };

  const cerrarModalProcesar = () => {
    setModalProcesar(null);
    setUbicaciones([]);
    setAlmacenSeleccionado("");
  };

  const iniciarRechazo = (req) => {
    setModalRechazo(req);
    setMotivoRechazo("");
  };

  const confirmarRechazo = async () => {
    if (!motivoRechazo.trim()) {
      setMensaje({ texto: "⚠️ Debe ingresar un motivo de rechazo", tipo: "error" });
      return;
    }
    try {
      await api.put(`/requisiciones/rechazar/${modalRechazo.id}`, { motivo: motivoRechazo });
      setMensaje({ texto: "✅ Requisición rechazada correctamente", tipo: "success" });
      setModalRechazo(null);
      setMotivoRechazo("");
      cargarRequisiciones();
      setTimeout(() => setMensaje({ texto: "", tipo: "" }), 5000);
    } catch (error) {
      setMensaje({
        texto: "❌ " + (error.response?.data?.mensaje || "Error al rechazar requisición"),
        tipo: "error"
      });
    }
  };

  const verDetalle = (requisicion) => {
    setModalDetalle(requisicion);
  };

  const cerrarModal = () => {
    setModalDetalle(null);
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case "pendiente":
      case "pendiente_procesar":
        return { bg: "#fef3c7", color: "#78350f", icon: "⏳", texto: "PENDIENTE" };
      case "aprobada":
      case "procesada":
        return { bg: "#dbeafe", color: "#1e40af", icon: "✔️", texto: "APROBADA" };
      case "rechazada":
        return { bg: "#fee2e2", color: "#991b1b", icon: "❌", texto: "RECHAZADA" };
      case "completada":
        return { bg: "#d1fae5", color: "#065f46", icon: "✅", texto: "COMPLETADA" };
      default:
        return { bg: "#f3f4f6", color: "#374151", icon: "📋", texto: estado?.toUpperCase() || "N/A" };
    }
  };

  useEffect(() => {
    cargarRequisiciones();

    const intervalo = setInterval(() => {
      cargarRequisiciones(true);
    }, 30000);

    return () => clearInterval(intervalo);
  }, []);

  const pendientes = requisiciones.filter(r => ["pendiente", "pendiente_procesar"].includes(r.estado));
  const aprobadas = requisiciones.filter(r => ["aprobada", "procesada"].includes(r.estado));
  const rechazadas = requisiciones.filter(r => r.estado === "rechazada");
  const completadas = requisiciones.filter(r => r.estado === "completada");

  // ✅ NUEVO: Agrupar requisiciones por ID
const agruparRequisiciones = (reqs) => {
  const grupos = {};
  
  reqs.forEach(req => {
    const reqId = req.id;
    
    if (!grupos[reqId]) {
      grupos[reqId] = {
        id: reqId,
        codigo: req.codigo,
        responsable: req.responsable,
        proyecto_id: req.proyecto_id,
        proyecto_nombre: req.proyecto_nombre,
        observacion: req.observacion,
        estado: req.estado,
        createdAt: req.createdAt,
        fecha_procesada: req.fecha_procesada,
        detalles: []
      };
    }
    
    // Agregar material al grupo
    grupos[reqId].detalles.push({
      material_id: req.material?.id,
      material_codigo: req.material?.codigo || req.codigo,
      material_nombre: req.material?.nombre || req.material?.descripcion,
      cantidad: req.cantidad
    });
  });
  
  return Object.values(grupos);
};

const pendientesAgrupadas = agruparRequisiciones(pendientes);
const aprobadasAgrupadas = agruparRequisiciones(aprobadas);
const rechazadasAgrupadas = agruparRequisiciones(rechazadas);
const completadasAgrupadas = agruparRequisiciones(completadas);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📋 Requisiciones de Departamentos</h1>
          <p style={styles.subtitle}>Procesar requisiciones y crear salidas para aprobación</p>
        </div>
      </div>

      {/* Estadísticas */}
      <div style={styles.statsContainer}>
        <div style={{...styles.statCard, borderLeft: "4px solid #f59e0b"}}>
          <div style={styles.statIcon}>⏳</div>
          <div>
            <div style={styles.statValue}>{pendientes.length}</div>
            <div style={styles.statLabel}>Pendientes</div>
          </div>
        </div>

        <div style={{...styles.statCard, borderLeft: "4px solid #3b82f6"}}>
          <div style={styles.statIcon}>✔️</div>
          <div>
            <div style={styles.statValue}>{aprobadas.length}</div>
            <div style={styles.statLabel}>Aprobadas</div>
          </div>
        </div>

        <div style={{...styles.statCard, borderLeft: "4px solid #ef4444"}}>
          <div style={styles.statIcon}>❌</div>
          <div>
            <div style={styles.statValue}>{rechazadas.length}</div>
            <div style={styles.statLabel}>Rechazadas</div>
          </div>
        </div>

        <div style={{...styles.statCard, borderLeft: "4px solid #10b981"}}>
          <div style={styles.statIcon}>✅</div>
          <div>
            <div style={styles.statValue}>{completadas.length}</div>
            <div style={styles.statLabel}>Completadas</div>
          </div>
        </div>
      </div>

      {/* Mensaje */}
      {mensaje.texto && (
        <div style={{
          ...styles.message,
          ...(mensaje.tipo === "success" ? styles.messageSuccess : styles.messageError)
        }}>
          {mensaje.texto}
        </div>
      )}

      {/* Información */}
      <div style={styles.infoCard}>
        <p style={styles.infoText}>
          💡 <strong>Nota:</strong> Al procesar una requisición, deberás seleccionar el almacén de origen
          y se creará automáticamente una SALIDA que será enviada al supervisor para aprobación.
        </p>
      </div>

      {/* Tabla */}
      <div style={styles.card}>
      <h3 style={styles.cardTitle}>
  ⏳ Requisiciones Pendientes de Procesar ({pendientesAgrupadas.length})
     </h3>

        {loading ? (
          <div style={styles.loadingMessage}>⏳ Cargando requisiciones...</div>
       ) : pendientesAgrupadas.length === 0 ? (
          <div style={styles.emptyMessage}>
            📭 No hay requisiciones pendientes de procesar.
            <br />
            <small>Las requisiciones aparecerán aquí cuando los departamentos las creen.</small>
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
             <thead>
  <tr style={styles.tableHeaderRow}>
    <th style={styles.th}>#</th>
    <th style={styles.th}>Código</th>
    <th style={styles.th}>Materiales</th>
    <th style={styles.th}>Departamento</th>
    <th style={styles.th}>Proyecto</th>
    <th style={styles.th}>Fecha</th>
    <th style={styles.th}>Estado</th>
    <th style={styles.th}>Acciones</th>
  </tr>
</thead>
             <tbody>
  {pendientesAgrupadas.map((req, idx) => {
    const badge = getEstadoBadge(req.estado);
    const totalMateriales = req.detalles.length;
    
    return (
      <tr key={req.id} style={styles.tr}>
        <td style={styles.td}>{idx + 1}</td>
        <td style={styles.td}><strong>{req.codigo}</strong></td>
        <td style={styles.td}>
          <strong>{totalMateriales} material(es)</strong>
          <br />
          <small style={{color: '#64748b'}}>
            {req.detalles.slice(0, 2).map(d => d.material_codigo).join(', ')}
            {totalMateriales > 2 && '...'}
          </small>
        </td>
        <td style={styles.td}>{req.responsable || 'Sin departamento'}</td>
        <td style={styles.td}>
          {req.proyecto_nombre ? (
            <>🏗️ {req.proyecto_nombre}</>
          ) : (
            <span style={{color: '#94a3b8'}}>Sin proyecto</span>
          )}
        </td>
        <td style={styles.td}>
          {new Date(req.createdAt).toLocaleDateString('es-DO')}
        </td>
        <td style={styles.td}>
          <span style={{
            ...styles.badge,
            background: badge.bg,
            color: badge.color
          }}>
            {badge.icon} {badge.texto}
          </span>
        </td>
        <td style={styles.tdActions}>
          <button
            style={styles.btnVer}
            onClick={() => verDetalle(req)}
          >
            👁️ Ver
          </button>
          <button
            style={styles.btnProcesar}
            onClick={() => iniciarProcesamiento(req)}
          >
            ✅ Aprobar
          </button>
          <button
            style={{...styles.btnProcesar, background: '#ef4444', marginLeft: '6px'}}
            onClick={() => iniciarRechazo(req)}
          >
            ❌ Rechazar
          </button>
        </td>
      </tr>
    );
  })}
</tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tabla de Aprobadas */}
      {aprobadas.length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>
            ✔️ Requisiciones Aprobadas — Pendiente aprobación supervisor ({aprobadasAgrupadas.length})
          </h3>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeaderRow}>
                  <th style={styles.th}>Código</th>
                  <th style={styles.th}>Materiales</th>
                  <th style={styles.th}>Departamento</th>
                  <th style={styles.th}>Fecha Aprobada</th>
                </tr>
              </thead>
              <tbody>
                {aprobadasAgrupadas.map((req) => (
                  <tr key={req.id} style={styles.tr}>
                    <td style={styles.td}><strong>{req.codigo}</strong></td>
                    <td style={styles.td}>
                      {req.detalles.length} material(es)
                      <br />
                      <small style={{color: '#64748b'}}>
                        {req.detalles.slice(0, 2).map(d => d.material_codigo).join(', ')}
                        {req.detalles.length > 2 && '...'}
                      </small>
                    </td>
                    <td style={styles.td}>{req.responsable || 'Sin departamento'}</td>
                    <td style={styles.td}>
                      {req.fecha_procesada
                        ? new Date(req.fecha_procesada).toLocaleDateString('es-DO')
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabla de Rechazadas */}
      {rechazadas.length > 0 && (
        <div style={styles.card}>
          <h3 style={{...styles.cardTitle, color: '#991b1b'}}>
            ❌ Requisiciones Rechazadas ({rechazadasAgrupadas.length})
          </h3>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeaderRow}>
                  <th style={styles.th}>Código</th>
                  <th style={styles.th}>Materiales</th>
                  <th style={styles.th}>Departamento</th>
                  <th style={styles.th}>Motivo</th>
                  <th style={styles.th}>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {rechazadasAgrupadas.map((req) => (
                  <tr key={req.id} style={{...styles.tr, background: '#fff5f5'}}>
                    <td style={styles.td}><strong>{req.codigo}</strong></td>
                    <td style={styles.td}>
                      {req.detalles.length} material(es)
                      <br />
                      <small style={{color: '#64748b'}}>
                        {req.detalles.slice(0, 2).map(d => d.material_codigo).join(', ')}
                        {req.detalles.length > 2 && '...'}
                      </small>
                    </td>
                    <td style={styles.td}>{req.responsable || 'Sin departamento'}</td>
                    <td style={styles.td}>
                      <small style={{color: '#991b1b'}}>
                        {req.observacion?.includes('Motivo rechazo:')
                          ? req.observacion.split('Motivo rechazo:').pop().trim()
                          : req.observacion || 'Sin motivo'}
                      </small>
                    </td>
                    <td style={styles.td}>
                      {req.fecha_procesada
                        ? new Date(req.fecha_procesada).toLocaleDateString('es-DO')
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabla de Completadas */}
      {completadas.length > 0 && (
        <div style={styles.card}>
          <h3 style={{...styles.cardTitle, color: '#065f46'}}>
            ✅ Requisiciones Completadas ({completadasAgrupadas.length})
          </h3>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeaderRow}>
                  <th style={styles.th}>Código</th>
                  <th style={styles.th}>Materiales</th>
                  <th style={styles.th}>Departamento</th>
                  <th style={styles.th}>Fecha Completada</th>
                </tr>
              </thead>
              <tbody>
                {completadasAgrupadas.map((req) => (
                  <tr key={req.id} style={{...styles.tr, background: '#f0fdf4'}}>
                    <td style={styles.td}><strong>{req.codigo}</strong></td>
                    <td style={styles.td}>
                      {req.detalles.length} material(es)
                      <br />
                      <small style={{color: '#64748b'}}>
                        {req.detalles.slice(0, 2).map(d => d.material_codigo).join(', ')}
                        {req.detalles.length > 2 && '...'}
                      </small>
                    </td>
                    <td style={styles.td}>{req.responsable || 'Sin departamento'}</td>
                    <td style={styles.td}>
                      {req.fecha_procesada
                        ? new Date(req.fecha_procesada).toLocaleDateString('es-DO')
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ✅ NUEVO: Modal para seleccionar almacén */}
      {modalProcesar && (
        <div style={styles.modalOverlay} onClick={cerrarModalProcesar}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>📦 Seleccionar Almacén de Origen</h3>
              <button style={styles.btnCerrar} onClick={cerrarModalProcesar}>✕</button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.requisicionInfo}>
                <h4 style={{margin: '0 0 15px 0', color: '#1e293b'}}>
                  Requisición: {modalProcesar.codigo}
                </h4>
                <p style={{margin: '0 0 10px 0', color: '#64748b', fontSize: '14px'}}>
                  <strong>Material:</strong> {modalProcesar.material?.nombre}
                </p>
                <p style={{margin: '0 0 20px 0', color: '#64748b', fontSize: '14px'}}>
                  <strong>Cantidad solicitada:</strong> {modalProcesar.cantidad}
                </p>
              </div>

              {loadingUbicaciones ? (
                <div style={{textAlign: 'center', padding: '20px', color: '#64748b'}}>
                  ⏳ Cargando ubicaciones...
                </div>
              ) : ubicaciones.length === 0 ? (
                <div style={{textAlign: 'center', padding: '20px', color: '#991b1b', background: '#fee2e2', borderRadius: '8px'}}>
                  ⚠️ No hay stock disponible en ningún almacén
                </div>
              ) : (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Almacén de Origen <span style={{color: '#ef4444'}}>*</span>
                    </label>
                    <select
                      style={styles.select}
                      value={almacenSeleccionado}
                      onChange={(e) => setAlmacenSeleccionado(e.target.value)}
                    >
                      <option value="">-- Seleccione un almacén --</option>
                      {ubicaciones.map((ub) => (
                        <option key={ub.almacen_id} value={ub.almacen_id}>
                          {ub.almacen_nombre} 
                          {ub.proyecto_nombre ? ` (${ub.proyecto_nombre})` : ' (General)'}
                          {' '}- Stock: {ub.cantidad}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.ubicacionesTable}>
                    <h4 style={{margin: '0 0 15px 0', fontSize: '14px', color: '#475569'}}>
                      📍 Stock disponible por almacén:
                    </h4>
                    <table style={{width: '100%', fontSize: '13px'}}>
                      <thead>
                        <tr style={{borderBottom: '2px solid #e2e8f0'}}>
                          <th style={{padding: '8px', textAlign: 'left', color: '#64748b'}}>Almacén</th>
                          <th style={{padding: '8px', textAlign: 'left', color: '#64748b'}}>Proyecto</th>
                          <th style={{padding: '8px', textAlign: 'right', color: '#64748b'}}>Stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ubicaciones.map((ub) => (
                          <tr 
                            key={ub.almacen_id}
                            style={{
                              borderBottom: '1px solid #f1f5f9',
                              background: almacenSeleccionado == ub.almacen_id ? '#eff6ff' : 'white'
                            }}
                          >
                            <td style={{padding: '10px'}}>{ub.almacen_nombre}</td>
                            <td style={{padding: '10px', color: '#64748b'}}>
                              {ub.proyecto_nombre || 'Sin asignar'}
                            </td>
                            <td style={{padding: '10px', textAlign: 'right', fontWeight: '600'}}>
                              {ub.cantidad}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <div style={styles.modalActions}>
                <button
                  style={styles.btnCancelar}
                  onClick={cerrarModalProcesar}
                >
                  Cancelar
                </button>
                <button
                  style={{
                    ...styles.btnModalProcesar,
                    opacity: !almacenSeleccionado ? 0.5 : 1,
                    cursor: !almacenSeleccionado ? 'not-allowed' : 'pointer'
                  }}
                  onClick={confirmarProcesamiento}
                  disabled={!almacenSeleccionado}
                >
                  ✅ Confirmar Procesamiento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalle (existente) */}
      {modalDetalle && (
        <div style={styles.modalOverlay} onClick={cerrarModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>📋 Detalle de Requisición</h3>
              <button style={styles.btnCerrar} onClick={cerrarModal}>✕</button>
            </div>
            <div style={styles.modalBody}>
             <div style={styles.detalleGrid}>
  <div style={styles.detalleItem}>
    <span style={styles.detalleLabel}>Código</span>
    <span style={styles.detalleValue}>{modalDetalle.codigo}</span>
  </div>
  <div style={styles.detalleItem}>
    <span style={styles.detalleLabel}>Departamento</span>
    <span style={styles.detalleValue}>{modalDetalle.responsable}</span>
  </div>
  {modalDetalle.proyecto_id && (
    <div style={styles.detalleItem}>
      <span style={styles.detalleLabel}>Proyecto</span>
      <span style={styles.detalleValue}>
        🏗️ {modalDetalle.proyecto_nombre || `Proyecto #${modalDetalle.proyecto_id}`}
      </span>
    </div>
  )}
                <div style={styles.detalleItem}>
                 <span style={styles.detalleLabel}>Total Materiales</span>
                 <span style={styles.detalleValue}>
                  {modalDetalle.detalles?.length || 0}
              </span>
            </div>
                <div style={styles.detalleItem}>
                  <span style={styles.detalleLabel}>Cantidad</span>
                  <span style={styles.detalleValue}>{modalDetalle.cantidad}</span>
                </div>
                <div style={styles.detalleItem}>
    <span style={styles.detalleLabel}>Fecha Solicitud</span>
    <span style={styles.detalleValue}>
      {new Date(modalDetalle.createdAt).toLocaleDateString('es-DO')}
    </span>
  </div>
                <div style={styles.detalleItem}>
    <span style={styles.detalleLabel}>Estado</span>
    <span style={styles.detalleValue}>
      {getEstadoBadge(modalDetalle.estado).texto}
    </span>
  </div>
</div>

{/* ✅ NUEVA: Tabla de materiales */}
{modalDetalle.detalles && modalDetalle.detalles.length > 0 && (
  <div style={{marginTop: '20px'}}>
    <span style={styles.detalleLabel}>Materiales Solicitados</span>
    <div style={{marginTop: '10px', overflowX: 'auto'}}>
      <table style={{width: '100%', fontSize: '13px', borderCollapse: 'collapse'}}>
        <thead>
          <tr style={{background: '#f8fafc'}}>
            <th style={{padding: '10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0'}}>Código</th>
            <th style={{padding: '10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0'}}>Material</th>
            <th style={{padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0'}}>Cantidad</th>
          </tr>
        </thead>
        <tbody>
          {modalDetalle.detalles.map((det, idx) => (
            <tr key={idx}>
              <td style={{padding: '10px', borderBottom: '1px solid #f1f5f9', fontWeight: '500'}}>
                {det.material_codigo}
              </td>
              <td style={{padding: '10px', borderBottom: '1px solid #f1f5f9'}}>
                {det.material_nombre}
              </td>
              <td style={{padding: '10px', textAlign: 'center', borderBottom: '1px solid #f1f5f9', fontWeight: '600'}}>
                {det.cantidad}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}

              {modalDetalle.observacion && (
                <div style={{marginTop: '20px'}}>
                  <span style={styles.detalleLabel}>Observaciones</span>
                  <p style={{
                    margin: '10px 0 0 0',
                    padding: '15px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#475569',
                    lineHeight: '1.6'
                  }}>
                    {modalDetalle.observacion}
                  </p>
                </div>
              )}

              {["pendiente", "pendiente_procesar"].includes(modalDetalle.estado) && (
                <div style={styles.modalActions}>
                  <button
                    style={styles.btnModalProcesar}
                    onClick={() => {
                      cerrarModal();
                      iniciarProcesamiento(modalDetalle);
                    }}
                  >
                    ✅ Aprobar Requisición
                  </button>
                  <button
                    style={{...styles.btnModalProcesar, background: '#ef4444', marginLeft: '10px'}}
                    onClick={() => {
                      cerrarModal();
                      iniciarRechazo(modalDetalle);
                    }}
                  >
                    ❌ Rechazar Requisición
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Rechazo */}
      {modalRechazo && (
        <div style={styles.modalOverlay} onClick={() => setModalRechazo(null)}>
          <div style={{...styles.modalContent, maxWidth: '480px'}} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{...styles.modalTitle, color: '#991b1b'}}>❌ Rechazar Requisición</h3>
              <button style={styles.btnCerrar} onClick={() => setModalRechazo(null)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <p style={{marginBottom: '15px', color: '#374151'}}>
                <strong>Requisición:</strong> {modalRechazo.codigo}
              </p>
              <label style={{display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151'}}>
                Motivo de rechazo *
              </label>
              <textarea
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                placeholder="Explique el motivo del rechazo..."
                rows={4}
                style={{
                  width: '100%', padding: '10px', borderRadius: '8px',
                  border: '1px solid #d1d5db', fontSize: '14px',
                  resize: 'vertical', boxSizing: 'border-box'
                }}
              />
              <div style={{display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end'}}>
                <button
                  style={{padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer'}}
                  onClick={() => setModalRechazo(null)}
                >
                  Cancelar
                </button>
                <button
                  style={{padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#ef4444', color: 'white', fontWeight: '600', cursor: 'pointer'}}
                  onClick={confirmarRechazo}
                >
                  ❌ Confirmar Rechazo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc',
    padding: '20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  header: {
    marginBottom: '25px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 5px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '25px',
  },
  statCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
  },
  statIcon: {
    fontSize: '32px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
  },
  statLabel: {
    fontSize: '12px',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  message: {
    marginBottom: '20px',
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center',
  },
  messageSuccess: {
    background: '#d1fae5',
    color: '#065f46',
    border: '1px solid #10b981',
  },
  messageError: {
    background: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #ef4444',
  },
  infoCard: {
    background: '#eff6ff',
    border: '1px solid #3b82f6',
    borderRadius: '12px',
    padding: '15px 20px',
    marginBottom: '20px',
  },
  infoText: {
    margin: '0',
    fontSize: '13px',
    color: '#1e40af',
    lineHeight: '1.6',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '30px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 20px 0',
  },
  loadingMessage: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#64748b',
  },
  emptyMessage: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#64748b',
    background: '#f8fafc',
    borderRadius: '8px',
    lineHeight: '1.8',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  tableHeaderRow: {
    background: '#1e293b',
    color: 'white',
  },
  th: {
    padding: '12px 8px',
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: '2px solid #cbd5e1',
  },
  tr: {
    borderBottom: '1px solid #e2e8f0',
    transition: 'background 0.2s',
  },
  td: {
    padding: '10px 8px',
    color: '#475569',
  },
  tdNum: {
    padding: '10px 8px',
    color: '#475569',
    textAlign: 'right',
    fontWeight: 'bold',
  },
  tdActions: {
    padding: '10px 8px',
    display: 'flex',
    gap: '5px',
    justifyContent: 'center',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
  },
  btnVer: {
    padding: '6px 12px',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  btnProcesar: {
    padding: '6px 12px',
    background: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 30px',
    borderBottom: '1px solid #e2e8f0',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  btnCerrar: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: '#64748b',
    cursor: 'pointer',
    padding: '0',
    width: '30px',
    height: '30px',
  },
  modalBody: {
    padding: '30px',
  },
  requisicionInfo: {
    background: '#f8fafc',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '25px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '8px',
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '2px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  ubicacionesTable: {
    marginTop: '25px',
    padding: '20px',
    background: '#f8fafc',
    borderRadius: '8px',
  },
  detalleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    marginBottom: '30px',
  },
  detalleItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  detalleLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detalleValue: {
    fontSize: '15px',
    color: '#1e293b',
    fontWeight: '500',
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    paddingTop: '20px',
    borderTop: '1px solid #e2e8f0',
    marginTop: '20px',
  },
  btnCancelar: {
    padding: '12px 24px',
    background: '#94a3b8',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  btnModalProcesar: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    transition: 'all 0.2s',
  },
};

export default RequisicionesPendientes;